// client/src/components/plataforma/contas/ContaSettingsPanel.js - VERSÃO COMPLETA E CORRIGIDA (COM BANNER E CEP)
import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useAccountById, useMutateAccounts, useAccountI18n } from '../../../hooks/useAccounts';
import { useSegmentos } from '../../../hooks/useSegmentos';
import { useAuth } from '../../../context/auth/AuthProvider';
import useAlert from '../../../hooks/useAlert';
import { hasPermission } from '../../../utils/permissions';
import {
    Loader2, Image as ImageIcon,
    Copy, UploadCloud, Search as SearchIcon, CheckCircle2
} from 'lucide-react';
import { lookupCep } from '../../../utils/cepService';
import { buildContaPublicUrl, contaPublicUrlPrefix } from '../../../utils/contaPublicUrl';
import { FormStepper, FormStepNavigation } from '../../shared/FormStepper';

const STEP_LABELS = ['Perfil e Mídia', 'Dados Fiscais', 'Endereço', 'Redes Sociais', 'Segmentos', 'Idioma & Formato'];

// --- COMPONENTE PRINCIPAL ---
const ContaSettingsPanel = ({ accountId, isMyAccount, currentUser }) => {
    const { data: accountDetails, isLoading: isAccountLoading } = useAccountById(accountId);

    // ✅ DESTRUCTURING CORRETO DAS MUTATIONS
    const { updateAccount, updateMyAccount, uploadAccountLogo, uploadAccountBanner, updateAccountI18n } = useMutateAccounts();
    // ✅ CORREÇÃO: mesmo achado do saveI18nData abaixo — sem passar o
    // accountId aqui, o formulário desta aba sempre lia /contas/my/i18n,
    // nunca os dados reais da conta sendo editada quando isMyAccount=false.
    const { data: i18nConfig, isLoading: isLoadingI18n } = useAccountI18n(isMyAccount ? undefined : accountId);

    const { data: segmentosDisponiveis = [], isLoading: isSegmentosLoading } = useSegmentos();
    
    const { setAlert } = useAlert();
    const { user } = useAuth();
    
    // Delega pro util central de permissões (que já cobre os bypasses de
    // super_admin/matriz/franquia/conta full_access) em vez de reimplementar
    // com uma lista de nomes de role fixa e frágil, que podia divergir da
    // regra real do sistema.
    const canEdit = isMyAccount || hasPermission(user, 'configuracoes_conta:edit');

    const [currentStep, setCurrentStep] = useState(1);
    const [i18nFormData, setI18nFormData] = useState({
        locale: 'pt-BR',
        dateFormat: 'DD/MM/YYYY',
        timeFormat: '24h',
        currency: 'BRL',
        firstDayOfWeek: 0
    });
    const [formData, setFormData] = useState({
        name: '', 
        slug: '', 
        nomeFantasia: '', 
        email: '', 
        telefone: '', 
        cnpj: '', 
        timezone: 'America/Sao_Paulo',
        redesSociais: { instagram: '', facebook: '', tiktok: '', youtube: '' },
        segmentoAtuacao: [],
        address: { zipCode: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '', country: 'Brasil' }
    });

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [bannerFile, setBannerFile] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(null);
    const [isCepLoading, setIsCepLoading] = useState(false);
    const [cepEncontrado, setCepEncontrado] = useState(false);

    const generateSlug = useCallback((name) => {
        if (!name) return '';
        return name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }, []);

    useEffect(() => {
        if (accountDetails) {
            setFormData({
                name: accountDetails.name || '',
                slug: accountDetails.slug || generateSlug(accountDetails.name),
                nomeFantasia: accountDetails.nomeFantasia || '',
                email: accountDetails.email || '',
                telefone: accountDetails.telefone || accountDetails.phone || '',
                cnpj: accountDetails.cnpj || '',
                timezone: accountDetails.configuracoesGerais?.timezone || accountDetails.settings?.timezone || 'America/Sao_Paulo',
                redesSociais: {
                    instagram: accountDetails.redesSociais?.instagram || '',
                    facebook: accountDetails.redesSociais?.facebook || '',
                    tiktok: accountDetails.redesSociais?.tiktok || '',
                    youtube: accountDetails.redesSociais?.youtube || ''
                },
                segmentoAtuacao: accountDetails.segmentoAtuacao || [],
                address: {
                    zipCode: accountDetails.address?.zipCode || '',
                    street: accountDetails.address?.street || '',
                    number: accountDetails.address?.number || '',
                    complement: accountDetails.address?.complement || '',
                    neighborhood: accountDetails.address?.neighborhood || '',
                    city: accountDetails.address?.city || '',
                    state: accountDetails.address?.state || '',
                    country: accountDetails.address?.country || 'Brasil'
                }
            });
            
            if (accountDetails.logoUrl || accountDetails.logo) {
                const url = accountDetails.logoUrl || (typeof accountDetails.logo === 'string' ? `https://onmicare.s3.us-east-2.amazonaws.com/${accountDetails.logo}` : accountDetails.logo?.url);
                setLogoPreview(url);
            }
            if (accountDetails.banner?.url || accountDetails.banner) {
                const url = accountDetails.banner?.url || (typeof accountDetails.banner === 'string' ? `https://onmicare.s3.us-east-2.amazonaws.com/${accountDetails.banner}` : accountDetails.banner?.url);
                setBannerPreview(url);
            }
        }
    }, [accountDetails, generateSlug]);

    useEffect(() => {
        if (i18nConfig) {
            setI18nFormData({
                locale: i18nConfig.locale || 'pt-BR',
                dateFormat: i18nConfig.dateFormat || 'DD/MM/YYYY',
                timeFormat: i18nConfig.timeFormat || '24h',
                currency: i18nConfig.currency || 'BRL',
                firstDayOfWeek: i18nConfig.firstDayOfWeek || 0
            });
        }
    }, [i18nConfig]);

    const onChangeI18n = (event) => {
        const { name, value, type } = event.target;
        setI18nFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseInt(value, 10) : value
        }));
    };

    useEffect(() => {
        if (formData.name && (!formData.slug || formData.slug === generateSlug(formData.name))) {
            const newSlug = generateSlug(formData.name);
            if (newSlug !== formData.slug) {
                setFormData(previous => ({ ...previous, slug: newSlug }));
            }
        }
    }, [formData.name, formData.slug, generateSlug]);

    const onChange = (event) => {
        const { name, value } = event.target;
        setFormData(previous => ({ ...previous, [name]: value }));
    };
    
    const onChangeNested = (event, parent) => {
        const { name, value } = event.target;
        if (parent === 'address' && name === 'zipCode') setCepEncontrado(false);
        setFormData(previous => ({
            ...previous,
            [parent]: { ...previous[parent], [name]: value }
        }));
    };

    const onSegmentoChange = (segmentoId) => {
        const segmentosAtuais = formData.segmentoAtuacao || [];
        const novosSegmentos = segmentosAtuais.includes(segmentoId)
            ? segmentosAtuais.filter(id => id !== segmentoId)
            : [...segmentosAtuais, segmentoId];
        setFormData(previous => ({ ...previous, segmentoAtuacao: novosSegmentos }));
    };

    const handleCepLookup = async () => {
        const cep = (formData.address.zipCode || '').replace(/\D/g, '');
        if (cep.length !== 8) {
            setAlert('CEP deve conter 8 dígitos', 'warning');
            return;
        }
        
        setIsCepLoading(true);
        try {
            const data = await lookupCep(cep);
            setFormData(previous => ({ 
                ...previous, 
                address: {
                    ...previous.address,
                    street: data.logradouro || previous.address.street, 
                    neighborhood: data.bairro || previous.address.neighborhood, 
                    city: data.localidade || previous.address.city, 
                    state: data.uf || previous.address.state 
                }
            }));
            setCepEncontrado(true);
            setAlert('Endereço preenchido automaticamente!', 'success');
        } catch (error) {
            setAlert(error.message, 'danger');
        } finally {
            setIsCepLoading(false);
        }
    };

    const onLogoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) return setAlert('Selecione uma imagem válida.', 'danger');
            if (file.size > 5 * 1024 * 1024) return setAlert('O logo deve ter menos de 5MB.', 'danger');
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const onBannerChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) return setAlert('Selecione uma imagem válida.', 'danger');
            if (file.size > 8 * 1024 * 1024) return setAlert('O banner deve ter menos de 8MB.', 'danger');
            setBannerFile(file);
            setBannerPreview(URL.createObjectURL(file));
        }
    };
    
    const saveAccountData = async () => {
        try {
            if (isMyAccount) await updateMyAccount.mutateAsync(formData);
            else await updateAccount.mutateAsync({ accountId: accountId, accountData: formData });
            return true;
        } catch (error) {
            setAlert(error.response?.data?.error || 'Erro ao salvar configurações', 'danger');
            return false;
        }
    };

    // ✅ ACHADO CRÍTICO: mandava i18nFormData direto como variável da
    // mutation, mas updateAccountI18n espera { accountId, i18nData } (ver
    // useAccounts.js). Sem o wrapper, i18nData chegava undefined no
    // mutationFn — o PUT saía com corpo vazio, o backend (que só atualiza
    // campos truthy no body) não mudava nada, e mesmo assim o
    // onSuccess/onError da mutation não disparava erro nenhum: a tela
    // mostrava "salvo com sucesso" mas nada persistia. accountId também
    // nunca era passado, então mesmo essa chamada quebrada sempre batia em
    // /contas/my/i18n — nunca em /contas/:id/i18n quando um admin de
    // matriz/franquia edita a conta de outra pessoa (isMyAccount=false).
    const saveI18nData = async () => {
        try {
            await updateAccountI18n.mutateAsync({
                accountId: isMyAccount ? undefined : accountId,
                i18nData: i18nFormData
            });
            return true;
        } catch (error) {
            setAlert(error.response?.data?.error || 'Erro ao atualizar configurações de idioma', 'danger');
            return false;
        }
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!canEdit) return setAlert('Você não tem permissão para editar as configurações da conta.', 'danger');

        const accountOk = await saveAccountData();
        if (!accountOk) return;
        const i18nOk = await saveI18nData();
        if (i18nOk) setAlert('Configurações da conta salvas com sucesso!', 'success');
    };

    const onMediaSubmit = (event, type) => {
        event.preventDefault();
        if (!canEdit) return setAlert('Você não tem permissão para alterar mídias da conta.', 'danger');
        
        const file = type === 'logo' ? logoFile : bannerFile;
        if (!file) return setAlert(`Selecione uma imagem para o ${type} primeiro.`, 'warning');

        // ✅ DISTINÇÃO DE MUTATIONS PARA LOGO E BANNER
        const mutation = type === 'logo' ? uploadAccountLogo : uploadAccountBanner;

        if (!mutation) {
            return setAlert(`Erro interno: Mutation de upload de ${type} não encontrada.`, 'danger');
        }

        mutation.mutate({ file, isMyAccount, accountId }, {
            onSuccess: () => {
                setAlert(`${type === 'logo' ? 'Logo' : 'Banner'} atualizado com sucesso!`, 'success');
                if (type === 'logo') setLogoFile(null);
                else setBannerFile(null);
                setTimeout(() => window.location.reload(), 1000);
            },
            onError: (error) => setAlert(error.response?.data?.error || `Erro ao fazer upload do ${type}`, 'danger')
        });
    };
    
    // Link real da página pública da conta (situai.site/conta/{slug}, ou
    // {slugFranquia}.situai.site/{slug} quando vinculada a uma franquia).
    // Bug antigo: usava REACT_APP_BOOKING_URL || 'https://agenda.onmicare.com',
    // domínio que não existe e ignorava a franquia.
    const bookingLinkPrefix = contaPublicUrlPrefix(accountDetails);
    const bookingLink = buildContaPublicUrl(accountDetails, formData.slug);

    const copyToClipboard = () => {
        if (!bookingLink) {
            setAlert('Defina o link (slug) da conta antes de copiar.', 'warning');
            return;
        }
        navigator.clipboard.writeText(bookingLink);
        setAlert('Link copiado para a área de transferência!', 'success');
    };

    const isLoading = isAccountLoading;
    const isSaving = updateAccount.isPending || updateMyAccount.isPending || updateAccountI18n.isPending;
    const isUploading = uploadAccountLogo?.isPending || uploadAccountBanner?.isPending;

    if (isLoading && !accountDetails) {
        return (
            <div className="panel-state-wrapper">
                <Loader2 className="animate-spin text-primary-600" size={40} />
                <p className="mt-3 text-gray-600">Carregando configurações da conta...</p>
            </div>
        );
    }

    const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.nomeFantasia || formData.name || 'C')}&background=random&color=fff&size=128`;

    return (
        <div className="content-panel">
            <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

            <div className="tab-content">
                <form onSubmit={onSubmit}>
                    {currentStep === 1 && (
                        <fieldset className="form-section" disabled={!canEdit || isSaving}>
                           <legend className="form-section-title">Perfil Público e Mídia</legend>
                            
                            <div className="form-grid-container mb-6">
                                <div className="form-group">
                                    <label htmlFor="name">Nome da Conta*</label>
                                    <input type="text" name="name" id="name" value={formData.name || ''} onChange={onChange} required className="form-input" placeholder="Nome oficial da sua empresa" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="nomeFantasia">Nome Fantasia</label>
                                    <input type="text" name="nomeFantasia" id="nomeFantasia" value={formData.nomeFantasia || ''} onChange={onChange} className="form-input" placeholder="Nome comercial" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Link da Página Pública</label>
                                    <div className="flex items-center">
                                        <span className="form-input-prefix">{bookingLinkPrefix}</span>
                                        <div className="form-input-suffix bg-gray-100 px-3 py-2 border border-gray-300 rounded-r-md flex-1 truncate">
                                            {formData.slug || 'seu-link'}
                                        </div>
                                        <button type="button" onClick={copyToClipboard} className="action-button action-button-secondary -ml-px" title="Copiar link"><Copy size={16} /></button>
                                    </div>
                                    <p className="form-hint mt-1">
                                        Endereço onde os clientes acessam esta conta para agendar. O identificador (slug) é gerado a partir do nome — ajuste-o na aba <strong>Página Pública</strong>.
                                    </p>
                                </div>
                            </div>
                            
                            <hr className="my-6 border-gray-200" />
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Upload Logo */}
                                <div className="flex flex-col gap-3">
                                    <label className="font-medium text-gray-700">Logo da Empresa</label>
                                    <div className="flex items-center gap-4">
                                        <img src={logoPreview || fallbackAvatar} alt="Logo" className="w-20 h-20 rounded-full object-cover border shadow-sm" />
                                        <div className="flex-1">
                                            <input type="file" id="logo-upload" onChange={onLogoChange} accept="image/png, image/jpeg, image/webp" className="form-input text-sm" />
                                            <button type="button" onClick={(e) => onMediaSubmit(e, 'logo')} className="action-button action-button-secondary mt-2 text-sm" disabled={!logoFile || isUploading}>
                                                {uploadAccountLogo?.isPending ? <Loader2 className="animate-spin icon" size={14}/> : <UploadCloud className="icon" size={14}/>} Enviar Logo
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Upload Banner */}
                                <div className="flex flex-col gap-3">
                                    <label className="font-medium text-gray-700">Banner de Capa</label>
                                    <div className="flex flex-col gap-3">
                                        <div className="w-full h-24 bg-gray-100 border rounded-lg overflow-hidden flex items-center justify-center">
                                            {bannerPreview ? (
                                                <img src={bannerPreview} alt="Banner" className="w-full h-full object-cover" />
                                            ) : (
                                                <ImageIcon className="text-gray-400" size={32} />
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input type="file" id="banner-upload" onChange={onBannerChange} accept="image/png, image/jpeg, image/webp" className="form-input text-sm flex-1" />
                                            <button type="button" onClick={(e) => onMediaSubmit(e, 'banner')} className="action-button action-button-secondary text-sm whitespace-nowrap" disabled={!bannerFile || isUploading}>
                                                {uploadAccountBanner?.isPending ? <Loader2 className="animate-spin icon" size={14}/> : <UploadCloud className="icon" size={14}/>} Enviar Banner
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    )}

                    {currentStep === 3 && (
                        <fieldset className="form-section" disabled={!canEdit || isSaving}>
                            <legend className="form-section-title">Endereço da Matriz</legend>
                            <div className="form-grid-container">
                                <div className="form-group">
                                    <label htmlFor="zipCode">CEP</label>
                                    <div className="flex gap-2">
                                        <input type="text" name="zipCode" id="zipCode" value={formData.address.zipCode || ''} onChange={(e) => onChangeNested(e, 'address')} className="form-input" placeholder="00000-000" maxLength="9"/>
                                        <button type="button" onClick={handleCepLookup} className="action-button action-button-secondary" disabled={isCepLoading || !formData.address.zipCode}>
                                            {isCepLoading ? <Loader2 className="animate-spin" size={16}/> : <SearchIcon size={16}/>}
                                        </button>
                                    </div>
                                    {cepEncontrado ? (
                                        <div className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                            <CheckCircle2 size={13} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                                            Endereço preenchido automaticamente.
                                            <button type="button" onClick={() => setCepEncontrado(false)}
                                                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                                                Editar manualmente
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="form-hint">Digite o CEP e clique na lupa para preencher</div>
                                    )}
                                </div>
                                <div className="form-group full-width">
                                    <label htmlFor="street">Logradouro (Rua, Avenida, etc)</label>
                                    <input type="text" name="street" id="street" value={formData.address.street || ''} onChange={(e) => onChangeNested(e, 'address')} className="form-input"
                                        readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="number">Número</label>
                                    <input type="text" name="number" id="number" value={formData.address.number || ''} onChange={(e) => onChangeNested(e, 'address')} className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="complement">Complemento</label>
                                    <input type="text" name="complement" id="complement" value={formData.address.complement || ''} onChange={(e) => onChangeNested(e, 'address')} className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="neighborhood">Bairro</label>
                                    <input type="text" name="neighborhood" id="neighborhood" value={formData.address.neighborhood || ''} onChange={(e) => onChangeNested(e, 'address')} className="form-input"
                                        readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="city">Cidade</label>
                                    <input type="text" name="city" id="city" value={formData.address.city || ''} onChange={(e) => onChangeNested(e, 'address')} className="form-input"
                                        readOnly={cepEncontrado} style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="state">Estado (UF)</label>
                                    <input type="text" name="state" id="state" value={formData.address.state || ''} onChange={(e) => onChangeNested(e, 'address')} className="form-input" maxLength="2"
                                        style={{ textTransform: 'uppercase', ...(cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : {}) }}
                                        readOnly={cepEncontrado} />
                                </div>
                            </div>
                        </fieldset>
                    )}
                    
                    {currentStep === 2 && (
                        <fieldset className="form-section" disabled={!canEdit || isSaving}>
                            <legend className="form-section-title">Dados Fiscais e Contato Principal</legend>
                            <div className="form-grid-container">
                                <div className="form-group">
                                    <label htmlFor="cnpj">CNPJ</label>
                                    <input type="text" name="cnpj" id="cnpj" value={formData.cnpj || ''} onChange={onChange} className="form-input" placeholder="00.000.000/0000-00" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="email">Email de Contato Oficial</label>
                                    <input type="email" name="email" id="email" value={formData.email || ''} onChange={onChange} className="form-input" placeholder="contato@empresa.com" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="telefone">Telefone/WhatsApp Principal</label>
                                    <input type="tel" name="telefone" id="telefone" value={formData.telefone || ''} onChange={onChange} className="form-input" placeholder="(11) 99999-9999" />
                                </div>
                            </div>
                        </fieldset>
                    )}

                    {currentStep === 4 && (
                        <fieldset className="form-section" disabled={!canEdit || isSaving}>
                            <legend className="form-section-title">Redes Sociais</legend>
                            <div className="form-grid-container">
                                <div className="form-group"> 
                                    <label htmlFor="instagram">Instagram</label> 
                                    <input type="url" name="instagram" id="instagram" placeholder="https://instagram.com/seu-usuario" value={formData.redesSociais.instagram || ''} onChange={(event) => onChangeNested(event, 'redesSociais')} className="form-input" /> 
                                </div>
                                <div className="form-group"> 
                                    <label htmlFor="facebook">Facebook</label> 
                                    <input type="url" name="facebook" id="facebook" placeholder="https://facebook.com/sua-pagina" value={formData.redesSociais.facebook || ''} onChange={(event) => onChangeNested(event, 'redesSociais')} className="form-input" /> 
                                </div>
                                <div className="form-group"> 
                                    <label htmlFor="tiktok">TikTok</label> 
                                    <input type="url" name="tiktok" id="tiktok" placeholder="https://tiktok.com/@seu-usuario" value={formData.redesSociais.tiktok || ''} onChange={(event) => onChangeNested(event, 'redesSociais')} className="form-input" /> 
                                </div>
                                <div className="form-group"> 
                                    <label htmlFor="youtube">YouTube</label> 
                                    <input type="url" name="youtube" id="youtube" placeholder="https://youtube.com/sua-canal" value={formData.redesSociais.youtube || ''} onChange={(event) => onChangeNested(event, 'redesSociais')} className="form-input" /> 
                                </div>
                            </div>
                        </fieldset>
                    )}

                    {currentStep === 5 && (
                        <fieldset className="form-section" disabled={!canEdit || isSaving}>
                            <legend className="form-section-title">Segmentos de Atuação</legend>
                            {isSegmentosLoading ? (
                                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary-600" /></div>
                            ) : (
                                <div className="segmentos-container">
                                    {segmentosDisponiveis.map(segmento => (
                                        <div key={segmento._id} className="segmento-item">
                                            <input type="checkbox" id={`segmento-${segmento._id}`} checked={(formData.segmentoAtuacao || []).includes(segmento._id)} onChange={() => onSegmentoChange(segmento._id)} className="form-checkbox" />
                                            <label htmlFor={`segmento-${segmento._id}`} className="cursor-pointer">
                                                {segmento.nome}
                                                {segmento.descricao && <span className="block text-sm text-gray-600 mt-1">{segmento.descricao}</span>}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="form-hint mt-4">Selecione os segmentos que melhor representam sua empresa</p>
                        </fieldset>
                    )}

                    {currentStep === 6 && (
                        isLoadingI18n ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader2 className="animate-spin text-primary-600" size={32} />
                                <span className="ml-3 text-gray-600">Carregando configurações...</span>
                            </div>
                        ) : (
                            <fieldset className="form-section" disabled={!canEdit || isSaving}>
                                <legend className="form-section-title">Configurações de Idioma e Formato</legend>

                                <p className="text-sm text-gray-600 mb-6">
                                    Configure as preferências de idioma, formato de data, hora e moeda para sua conta.
                                </p>

                                <div className="form-grid-container">
                                    <div className="form-group">
                                        <label htmlFor="locale">Idioma Principal*</label>
                                        <select id="locale" name="locale" value={i18nFormData.locale} onChange={onChangeI18n} className="form-input" required>
                                            <option value="pt-BR">Português (Brasil)</option>
                                            <option value="en-US">English (US)</option>
                                            <option value="es-ES">Español (España)</option>
                                        </select>
                                        <p className="form-hint">Idioma padrão do sistema</p>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="dateFormat">Formato de Data*</label>
                                        <select id="dateFormat" name="dateFormat" value={i18nFormData.dateFormat} onChange={onChangeI18n} className="form-input" required>
                                            <option value="DD/MM/YYYY">DD/MM/AAAA (31/12/2024)</option>
                                            <option value="MM/DD/YYYY">MM/DD/AAAA (12/31/2024)</option>
                                            <option value="YYYY-MM-DD">AAAA-MM-DD (2024-12-31)</option>
                                        </select>
                                        <p className="form-hint">Como as datas serão exibidas</p>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="timeFormat">Formato de Hora*</label>
                                        <select id="timeFormat" name="timeFormat" value={i18nFormData.timeFormat} onChange={onChangeI18n} className="form-input" required>
                                            <option value="24h">24 horas (14:30)</option>
                                            <option value="12h">12 horas (2:30 PM)</option>
                                        </select>
                                        <p className="form-hint">Sistema de 12h ou 24h</p>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="currency">Moeda*</label>
                                        <select id="currency" name="currency" value={i18nFormData.currency} onChange={onChangeI18n} className="form-input" required>
                                            <option value="BRL">Real Brasileiro (R$)</option>
                                            <option value="USD">Dólar Americano ($)</option>
                                            <option value="EUR">Euro (€)</option>
                                        </select>
                                        <p className="form-hint">Moeda para valores monetários</p>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="firstDayOfWeek">Primeiro Dia da Semana*</label>
                                        <select id="firstDayOfWeek" name="firstDayOfWeek" value={i18nFormData.firstDayOfWeek} onChange={onChangeI18n} className="form-input" required>
                                            <option value={0}>Domingo</option>
                                            <option value={1}>Segunda-feira</option>
                                        </select>
                                        <p className="form-hint">Dia que inicia a semana no calendário</p>
                                    </div>
                                </div>

                                <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                                    <h4 className="font-medium text-gray-900 mb-3">Prévia das Configurações:</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Idioma:</span>
                                            <div className="font-medium">
                                                {i18nFormData.locale === 'pt-BR' && 'Português'}
                                                {i18nFormData.locale === 'en-US' && 'English'}
                                                {i18nFormData.locale === 'es-ES' && 'Español'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Data:</span>
                                            <div className="font-medium">
                                                {i18nFormData.dateFormat === 'DD/MM/YYYY' && '31/12/2024'}
                                                {i18nFormData.dateFormat === 'MM/DD/YYYY' && '12/31/2024'}
                                                {i18nFormData.dateFormat === 'YYYY-MM-DD' && '2024-12-31'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Hora:</span>
                                            <div className="font-medium">
                                                {i18nFormData.timeFormat === '24h' ? '14:30' : '2:30 PM'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Moeda:</span>
                                            <div className="font-medium">
                                                {i18nFormData.currency === 'BRL' && 'R$ 1.000,00'}
                                                {i18nFormData.currency === 'USD' && '$ 1,000.00'}
                                                {i18nFormData.currency === 'EUR' && '€ 1.000,00'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )
                    )}

                    {canEdit ? (
                        <FormStepNavigation
                            currentStep={currentStep}
                            totalSteps={STEP_LABELS.length}
                            onBack={() => setCurrentStep(s => s - 1)}
                            onNext={() => setCurrentStep(s => s + 1)}
                            isSubmitting={isSaving}
                            submitLabel="Salvar Alterações"
                        />
                    ) : (
                        <div className="form-actions">
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded w-full">
                                <p className="text-sm text-yellow-800"><strong>Atenção:</strong> Você não tem permissão para editar estas configurações.</p>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ContaSettingsPanel;